<template>
  <div class="space-y-2">
    <div
      v-if="dates.length > 0"
      class="flex flex-wrap items-center gap-2"
    >
      <span
        v-for="(d, i) in dates"
        :key="`${d}-${i}`"
        class="inline-flex items-center gap-1 rounded-full bg-bochas-rojo/20 border border-bochas-rojo text-white text-xs sm:text-sm px-2.5 py-1"
      >
        {{ formatChip(d) }}
        <button
          type="button"
          @click="removeDate(d)"
          class="text-white/80 hover:text-white"
          title="Quitar fecha"
        >
          ✕
        </button>
      </span>
    </div>

    <div
      class="rounded-xl bg-bochas-card-bg border border-white/10 p-3 sm:p-4 w-full"
    >
      <!-- Header mes -->
      <div class="flex items-center justify-between mb-3">
        <button
          type="button"
          @click="prevMonth"
          class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-white"
          aria-label="Mes anterior"
        >
          <IconFbochas name="chevron-left" :size="18" />
        </button>
        <p class="text-sm font-semibold text-white capitalize">
          {{ monthLabel }}
        </p>
        <button
          type="button"
          @click="nextMonth"
          class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-white"
          aria-label="Mes siguiente"
        >
          <IconFbochas name="chevron-right" :size="18" />
        </button>
      </div>

      <!-- Días de la semana -->
      <div class="grid grid-cols-7 gap-1 mb-1">
        <span
          v-for="d in weekDays"
          :key="d"
          class="text-center text-[10px] font-semibold uppercase text-gray-400 py-1"
        >
          {{ d }}
        </span>
      </div>

      <!-- Grid de días -->
      <div class="grid grid-cols-7 gap-1">
        <button
          v-for="cell in cells"
          :key="cell.key"
          type="button"
          :disabled="!cell.inMonth"
          @click="onDayClick(cell)"
          :class="[
            'h-9 sm:h-10 rounded-md text-sm transition-colors',
            !cell.inMonth
              ? 'text-gray-600 cursor-default'
              : cell.selected
              ? 'bg-bochas-rojo text-white font-semibold'
              : cell.inRange
              ? 'bg-bochas-rojo/30 text-white'
              : cell.isToday
              ? 'border border-bochas-rojo text-white hover:bg-white/10'
              : 'text-gray-200 hover:bg-white/10',
          ]"
        >
          {{ cell.day }}
        </button>
      </div>

      <p class="text-[11px] text-gray-500 mt-3">
        Hacé click en cada día que se juegue. Se agregan como fechas
        individuales y se ordenan automáticamente.
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import IconFbochas from "@/components/Icons/IconFbochas.vue";

const props = defineProps({
  modelValue: { type: Array, default: () => [] }, // ["YYYY-MM-DD", ...]
});
const emit = defineEmits(["update:modelValue"]);

const MONTHS_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
const weekDays = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const dates = computed(() => props.modelValue || []);

const view = ref({ y: 1970, m: 0 });

const monthLabel = computed(
  () => `${MONTHS_ES[view.value.m]} ${view.value.y}`,
);

const today = new Date();
const todayKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;

const dateSet = computed(() => new Set(dates.value));

const cells = computed(() => {
  const y = view.value.y;
  const m = view.value.m;
  const firstOfMonth = new Date(y, m, 1);
  const firstDow = firstOfMonth.getDay();
  const start = new Date(y, m, 1 - firstDow);
  const arr = [];
  for (let i = 0; i < 42; i++) {
    const dt = new Date(start);
    dt.setDate(start.getDate() + i);
    const inMonth = dt.getMonth() === m;
    const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
    const isToday = key === todayKey;
    const iso = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
    arr.push({
      key,
      iso,
      day: dt.getDate(),
      inMonth,
      isToday,
      selected: dateSet.value.has(iso),
    });
  }
  return arr;
});

function pad(n) {
  return String(n).padStart(2, "0");
}

function prevMonth() {
  if (view.value.m === 0) {
    view.value = { y: view.value.y - 1, m: 11 };
  } else {
    view.value = { y: view.value.y, m: view.value.m - 1 };
  }
}
function nextMonth() {
  if (view.value.m === 11) {
    view.value = { y: view.value.y + 1, m: 0 };
  } else {
    view.value = { y: view.value.y, m: view.value.m + 1 };
  }
}

function onDayClick(cell) {
  if (!cell.inMonth) return;
  const current = dates.value.slice();
  const idx = current.indexOf(cell.iso);
  if (idx >= 0) {
    current.splice(idx, 1);
  } else {
    current.push(cell.iso);
  }
  // Orden ascendente y dedupe.
  const next = Array.from(new Set(current)).sort();
  emit("update:modelValue", next);
  // Llevar la vista al mes del día clickeado.
  const [y, m] = cell.iso.split("-").map((n) => parseInt(n, 10));
  if (y && m) view.value = { y, m: m - 1 };
}

function removeDate(iso) {
  const next = dates.value.filter((d) => d !== iso);
  emit("update:modelValue", next);
}

function formatChip(iso) {
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  return `${d} de ${MONTHS_ES[m - 1]}`;
}

// Inicializar y sincronizar la vista con la primera fecha.
watch(
  dates,
  (val) => {
    if (!val || val.length === 0) {
      view.value = { y: today.getFullYear(), m: today.getMonth() };
      return;
    }
    const first = [...val].sort()[0];
    const [y, m] = first.split("-").map((n) => parseInt(n, 10));
    if (y && m) view.value = { y, m: m - 1 };
  },
  { immediate: true },
);
</script>
